"use client";

import { useEffect, useId, useRef } from "react";
import { ExternalLink, Sparkles } from "lucide-react";
import { CopyButton, focusRing } from "../ui";
import { formatAbsolute } from "./format";

// ────────────────────────────────────────────────────────────────────────────
// SecretRevealCard — the one and only time a webhook signing secret is shown
// (after create or rotate). Mirrors RevealedKeyCard on API Settings.
//
// The secret lives in the parent's React state only: it is never logged,
// persisted, put in a URL or re-fetchable. Dismissing the card (or leaving
// the page) drops it for good.
// ────────────────────────────────────────────────────────────────────────────

/** A freshly issued signing secret waiting to be copied. */
export interface SecretReveal {
  /** Bumped by the owner on every reveal so a repeat rotation remounts the card. */
  nonce: number;
  endpointId: string;
  host: string;
  secret: string;
  kind: "created" | "rotated";
  /** Rotation only: when the previous secret stops verifying (null = revoked now). */
  previousSecretExpiresAt: string | null;
}

/**
 * @param reveal    what to show
 * @param onDismiss "I've copied it" — the owner drops the secret from state
 */
export default function SecretRevealCard({
  reveal,
  onDismiss,
}: {
  reveal: SecretReveal;
  onDismiss: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();

  // Move focus here when the card appears (after the create / rotate dialog
  // closes) so keyboard and screen-reader users land on the secret.
  useEffect(() => {
    ref.current?.focus();
  }, []);

  return (
    <div
      ref={ref}
      tabIndex={-1}
      aria-labelledby={titleId}
      role="region"
      className="mb-4 p-4 rounded-xl border border-[#61CE70]/30 bg-[#61CE70]/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#61CE70]/40"
    >
      <div className="flex items-start gap-3">
        <Sparkles className="w-4 h-4 text-[#61CE70] shrink-0 mt-1" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p id={titleId} className="text-white font-semibold text-sm break-words">
            Signing secret for {reveal.host} — save it now.
          </p>
          <p className="text-gray-400 text-xs mt-0.5 leading-relaxed">
            This is the only time you'll see this secret. Store it as an environment variable (for
            example <span className="font-mono text-gray-300">CASHTAP_WEBHOOK_SECRET</span>).
          </p>

          <div className="mt-3 flex items-center gap-2 p-2.5 rounded-lg bg-black/30 border border-gray-800">
            <code className="text-[#61CE70] font-mono text-xs sm:text-sm break-all flex-1 min-w-0 select-all">
              {reveal.secret}
            </code>
            <CopyButton text={reveal.secret} label="Copy signing secret" />
          </div>

          {reveal.kind === "rotated" ? (
            <p className="mt-2.5 text-xs text-gray-400 leading-relaxed">
              {reveal.previousSecretExpiresAt ? (
                <>
                  Your previous secret keeps verifying until{" "}
                  <time dateTime={reveal.previousSecretExpiresAt} className="text-gray-200">
                    {formatAbsolute(reveal.previousSecretExpiresAt)}
                  </time>
                  : until then every delivery carries both signatures, so deploy the new secret any time
                  before that.
                </>
              ) : (
                "The previous secret stopped working immediately. Deliveries fail verification until your server uses this one."
              )}
            </p>
          ) : (
            <p className="mt-2.5 text-xs text-gray-400 leading-relaxed">
              Next: verify the <span className="font-mono text-gray-300">X-CashTap-Signature</span> header
              with this secret on every request, then send a test event from the endpoint below.{" "}
              <a
                href="/developers/docs#webhooks"
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-1 text-[#61CE70] hover:text-white rounded-sm ${focusRing}`}
              >
                Signature guide <ExternalLink className="w-3 h-3" aria-hidden="true" />
              </a>
            </p>
          )}

          <button
            type="button"
            onClick={onDismiss}
            className={`mt-3 -ml-1 px-1 py-1.5 rounded-md text-xs text-gray-400 hover:text-white ${focusRing}`}
          >
            I've copied it
          </button>
        </div>
      </div>
    </div>
  );
}
